import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { companiesApi, supportingDataApi } from '@/services/api';
import { CompanyFormData, CompanyCategory, AssetClass, Sector, Country } from '@/types/client';
import FormCard, {
  FormSection,
  FormGrid,
  FormField,
  FormInput,
  FormSelect,
  FormCheckbox,
  FormCheckboxGroup,
} from '@/components/utils/FormCard';

const EQUITY_CLASS_NAME = 'Equity';
const INDICES_CLASS_NAME = 'Indicies';

type FormErrors = Partial<Record<keyof CompanyFormData, string>> & { general?: string };

const EMPTY_FORM: CompanyFormData = {
  name: '',
  symbol: '',
  mf_scheme_code: '',
  isin: '',
  bse_code: '',
  nse_code: '',
  accord_code: null,
  alpha_sense_code: '',
  country: null,
  restriction_for_nri: false,
  exclude_from_aum_calculation: false,
  is_active: true,
  asset_class: null,
  category: null,
  sector: null,
};

const CompanyForm: React.FC = () => {
  const navigate = useNavigate();
  const { id: companyIdParam } = useParams<{ id: string }>();
  const isEditMode = Boolean(companyIdParam);

  const [formData, setFormData] = useState<CompanyFormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loadingData, setLoadingData] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);

  const [categories, setCategories] = useState<CompanyCategory[]>([]);
  const [assetClasses, setAssetClasses] = useState<AssetClass[]>([]);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      supportingDataApi.getCompanyCategories(),
      supportingDataApi.getAssetClasses(),
      supportingDataApi.getCompanySectors({ params: { page_size: 500 } }),
      supportingDataApi.getCountries(),
    ])
      .then(([categoriesData, assetClassesData, sectorsData, countriesData]) => {
        if (cancelled) return;
        setCategories(categoriesData);
        setAssetClasses(assetClassesData);
        setSectors(sectorsData);
        setCountries(countriesData);
      })
      .catch((err) => {
        if (!cancelled) console.error('Error fetching supporting data:', err);
      });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!isEditMode || !companyIdParam) return;

    let cancelled = false;
    setLoadingData(true);

    companiesApi.getCompany(Number(companyIdParam))
      .then((company) => {
        if (cancelled) return;
        setFormData({
          name: company.name,
          symbol: company.symbol,
          mf_scheme_code: company.mf_scheme_code || '',
          isin: company.isin,
          bse_code: company.bse_code || '',
          nse_code: company.nse_code || '',
          accord_code: company.accord_code ?? null,
          alpha_sense_code: company.alpha_sense_code || '',
          country: company.country?.id ?? null,
          restriction_for_nri: company.restriction_for_nri,
          exclude_from_aum_calculation: company.exclude_from_aum_calculation,
          is_active: company.is_active,
          asset_class: company.asset_class?.id ?? null,
          category: company.category?.id ?? null,
          sector: company.sector?.id ?? null,
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setErrors({ general: 'Failed to fetch company details' });
          console.error('Error fetching company:', err);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingData(false);
      });

    return () => { cancelled = true; };
  }, [companyIdParam, isEditMode]);

  // Indian Equity companies must carry an Accord code; Indian Equity/Indices
  // companies must carry at least one exchange code. Mirrors CompanyFormSerializer.
  const { isAccordCodeRequired, isExchangeCodeRequired } = useMemo(() => {
    const selectedCountry = countries.find((c) => c.id === formData.country);
    const selectedAssetClass = assetClasses.find((a) => a.id === formData.asset_class);
    const isIndia = selectedCountry?.code === 'IND';
    return {
      isAccordCodeRequired: isIndia && selectedAssetClass?.name === EQUITY_CLASS_NAME,
      isExchangeCodeRequired: isIndia && (
        selectedAssetClass?.name === EQUITY_CLASS_NAME || selectedAssetClass?.name === INDICES_CLASS_NAME
      ),
    };
  }, [countries, assetClasses, formData.country, formData.asset_class]);

  const handleChange = <K extends keyof CompanyFormData>(field: K, value: CompanyFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined, general: undefined }));
  };

  const handleNumberChange = (field: 'accord_code', raw: string) => {
    const parsed = raw === '' ? null : parseInt(raw, 10);
    handleChange(field, parsed === null || Number.isNaN(parsed) ? null : parsed);
  };

  const handleSelectChange = (field: 'country' | 'asset_class' | 'category' | 'sector', raw: string) => {
    handleChange(field, raw === '' ? null : Number(raw));
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Company Name is required';
    if (!formData.symbol.trim()) newErrors.symbol = 'Symbol is required';
    if (!formData.isin.trim()) newErrors.isin = 'ISIN is required';
    if (!formData.country) newErrors.country = 'Country is required';
    if (!formData.asset_class) newErrors.asset_class = 'Asset Class is required';

    if (isAccordCodeRequired && formData.accord_code == null) {
      newErrors.accord_code = 'Accord Code is required for Indian Equity companies';
    } else if (formData.accord_code != null && (formData.accord_code < 100000 || formData.accord_code > 999999)) {
      newErrors.accord_code = 'Accord Code must be exactly 6 digits';
    }

    if (isExchangeCodeRequired && !formData.bse_code?.trim() && !formData.nse_code?.trim()) {
      const message = 'Either BSE Code or NSE Code is required for Indian Equity/Indices companies';
      newErrors.bse_code = message;
      newErrors.nse_code = message;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      if (isEditMode && companyIdParam) {
        await companiesApi.updateCompany(Number(companyIdParam), formData);
      } else {
        await companiesApi.createCompany(formData);
      }
      navigate('/companies');
    } catch (err: unknown) {
      const responseData = (err as { response?: { data?: unknown } })?.response?.data;
      const pick = (v: unknown) => (Array.isArray(v) ? String(v[0]) : String(v));

      const fieldErrors: FormErrors = {};
      const generalMessages: string[] = [];

      if (responseData && typeof responseData === 'object') {
        for (const [key, messages] of Object.entries(responseData as Record<string, unknown>)) {
          if (Object.prototype.hasOwnProperty.call(EMPTY_FORM, key)) {
            fieldErrors[key as keyof CompanyFormData] = pick(messages);
          } else {
            generalMessages.push(pick(messages));
          }
        }
      } else if (typeof responseData === 'string' && responseData.trim()) {
        generalMessages.push(responseData);
      }

      if (generalMessages.length > 0) {
        fieldErrors.general = generalMessages.join(' ');
      } else if (Object.keys(fieldErrors).length === 0) {
        fieldErrors.general = 'Failed to save company';
      }
      setErrors(fieldErrors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      console.error('Error saving company:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const submitLabel = submitting
    ? 'Saving…'
    : (isEditMode ? 'Update Company' : 'Create Company');

  return (
    <FormCard
      title={isEditMode ? 'Edit Company' : 'Add New Company'}
      subtitle={isEditMode
        ? 'Update company master details, classification and trading flags.'
        : 'Add a new company to the securities master.'}
      backLabel="← Back to Companies"
      onBack={() => navigate('/companies')}
      errorBanner={errors.general}
      submitLabel={submitLabel}
      onSubmit={handleSubmit}
      onCancel={() => navigate('/companies')}
      submitting={submitting}
      loading={loadingData}
      loadingMessage="Loading company data…"
      variant="page"
    >
      <FormSection title="Basic Information">
        <FormGrid>
          <FormField label="Company Name" required error={errors.name}>
            <FormInput
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Company name"
              hasError={Boolean(errors.name)}
            />
          </FormField>

          <FormField label="Symbol" required error={errors.symbol}>
            <FormInput
              type="text"
              value={formData.symbol}
              onChange={(e) => handleChange('symbol', e.target.value)}
              placeholder="e.g. RELIANCE"
              hasError={Boolean(errors.symbol)}
            />
          </FormField>

          <FormField label="ISIN" required error={errors.isin}>
            <FormInput
              type="text"
              value={formData.isin}
              onChange={(e) => handleChange('isin', e.target.value)}
              placeholder="e.g. INE002A01018"
              hasError={Boolean(errors.isin)}
            />
          </FormField>

          <FormField label="Country" required error={errors.country}>
            <FormSelect
              value={formData.country ?? ''}
              onChange={(e) => handleSelectChange('country', e.target.value)}
              hasError={Boolean(errors.country)}
            >
              <option value="">Select Country</option>
              {countries.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.name} ({country.code})
                </option>
              ))}
            </FormSelect>
          </FormField>
        </FormGrid>
      </FormSection>

      <FormSection title="Classification">
        <FormGrid>
          <FormField label="Asset Class" required error={errors.asset_class}>
            <FormSelect
              value={formData.asset_class ?? ''}
              onChange={(e) => handleSelectChange('asset_class', e.target.value)}
              hasError={Boolean(errors.asset_class)}
            >
              <option value="">Select Asset Class</option>
              {assetClasses.map((assetClass) => (
                <option key={assetClass.id} value={assetClass.id}>{assetClass.name}</option>
              ))}
            </FormSelect>
          </FormField>

          <FormField label="Category" error={errors.category}>
            <FormSelect
              value={formData.category ?? ''}
              onChange={(e) => handleSelectChange('category', e.target.value)}
              hasError={Boolean(errors.category)}
            >
              <option value="">Select Category</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>{category.category}</option>
              ))}
            </FormSelect>
          </FormField>

          <FormField label="Sector" error={errors.sector}>
            <FormSelect
              value={formData.sector ?? ''}
              onChange={(e) => handleSelectChange('sector', e.target.value)}
              hasError={Boolean(errors.sector)}
            >
              <option value="">Select Sector</option>
              {sectors.map((sector) => (
                <option key={sector.id} value={sector.id}>{sector.sector_name}</option>
              ))}
            </FormSelect>
          </FormField>
        </FormGrid>
      </FormSection>

      <FormSection
        title="Exchange & Identifiers"
        description="Indian Equity companies require an Accord Code; Indian Equity/Indices companies require at least one exchange code."
      >
        <FormGrid>
          <FormField label="BSE Code" required={isExchangeCodeRequired} error={errors.bse_code}>
            <FormInput
              type="text"
              value={formData.bse_code ?? ''}
              onChange={(e) => handleChange('bse_code', e.target.value)}
              placeholder="e.g. 500325"
              hasError={Boolean(errors.bse_code)}
            />
          </FormField>

          <FormField label="NSE Code" required={isExchangeCodeRequired} error={errors.nse_code}>
            <FormInput
              type="text"
              value={formData.nse_code ?? ''}
              onChange={(e) => handleChange('nse_code', e.target.value)}
              placeholder="e.g. RELIANCE"
              hasError={Boolean(errors.nse_code)}
            />
          </FormField>

          <FormField label="Accord Code" required={isAccordCodeRequired} error={errors.accord_code}>
            <FormInput
              type="number"
              value={formData.accord_code ?? ''}
              onChange={(e) => handleNumberChange('accord_code', e.target.value)}
              placeholder="6-digit code"
              hasError={Boolean(errors.accord_code)}
            />
          </FormField>

          <FormField label="MF Scheme Code" error={errors.mf_scheme_code}>
            <FormInput
              type="text"
              value={formData.mf_scheme_code ?? ''}
              onChange={(e) => handleChange('mf_scheme_code', e.target.value)}
              placeholder="Mutual fund scheme code"
              hasError={Boolean(errors.mf_scheme_code)}
            />
          </FormField>

          <FormField label="Alpha Sense Code" error={errors.alpha_sense_code}>
            <FormInput
              type="text"
              value={formData.alpha_sense_code ?? ''}
              onChange={(e) => handleChange('alpha_sense_code', e.target.value)}
              placeholder="AlphaSense identifier"
              hasError={Boolean(errors.alpha_sense_code)}
            />
          </FormField>
        </FormGrid>
      </FormSection>

      <FormSection title="Trading Settings">
        <FormCheckboxGroup>
          <FormCheckbox
            label="Is Active"
            hint="Inactive companies are hidden from order flows"
            checked={Boolean(formData.is_active)}
            onChange={(e) => handleChange('is_active', e.target.checked)}
          />
          <FormCheckbox
            label="NRI Restriction"
            hint="Restrict this company for NRI clients"
            checked={Boolean(formData.restriction_for_nri)}
            onChange={(e) => handleChange('restriction_for_nri', e.target.checked)}
          />
          <FormCheckbox
            label="Exclude from AUM Calculation"
            hint="Holdings are ignored when computing AUM"
            checked={Boolean(formData.exclude_from_aum_calculation)}
            onChange={(e) => handleChange('exclude_from_aum_calculation', e.target.checked)}
          />
        </FormCheckboxGroup>
      </FormSection>
    </FormCard>
  );
};

export default CompanyForm;
